import { DeliveryInstructions } from '../1_types/genericTypes';
import store from '../redux/store';
import { formatBasket, handleJoiSchemaError } from '../utils';
import { logHighError, logMediumError } from '../utils/logger';
import {
  API_PENDING_ORDER,
  API_PENDING_ORDER_BASKET,
  API_PENDING_ORDER_BASKET_ITEM,
  API_PENDING_ORDER_DELIVERY_INSTRUCTIONS,
  API_PENDING_ORDER_VALIDITY_CHECK,
} from './api';
import {
  sendDeleteRequest,
  sendGetRequest,
  sendPostRequest,
  sendPutRequest,
} from './backend';

export const updatePendingOrderDeliveryInstructions = async (
  userId: string,
  deliveryInstructions: DeliveryInstructions
): Promise<boolean> => {
  const { companyId } = store.getState().configurationReducer;

  try {
    await sendPutRequest(
      API_PENDING_ORDER_DELIVERY_INSTRUCTIONS(companyId, userId),
      deliveryInstructions
    );

    return true;
  } catch (error) {
    logMediumError(
      `Failed to update pending order delivery instruction for user (${userId}) for company (${companyId}) ${handleJoiSchemaError(
        error
      )}`
    );
    throw error;
  }
};

export const updatePendingOrderBasket = async (
  userId: string,
  data: any
): Promise<any> => {
  const { companyId } = store.getState().configurationReducer;
  try {
    const response = await sendPutRequest(
      API_PENDING_ORDER_BASKET(companyId, userId),
      formatBasket(data)
    );

    return response.data;
  } catch (error) {
    logMediumError(
      `Failed to update pending order basket for user (${userId}) for company (${companyId}) ${handleJoiSchemaError(
        error
      )}`
    );
    throw error;
  }
};

export const createPendingOrder = async (
  userId: string,
  data: any
): Promise<any> => {
  const { companyId } = store.getState().configurationReducer;
  try {
    const response = await sendPostRequest(
      API_PENDING_ORDER(companyId, userId),
      data,
      false
    );

    return response.data;
  } catch (error) {
    logMediumError(
      `Failed to create pending order for user (${userId}) for company (${companyId}) ${handleJoiSchemaError(
        error
      )}`
    );
    throw error;
  }
};

export const deletePendingOrder = async (userId: string): Promise<boolean> => {
  const { companyId } = store.getState().configurationReducer;

  try {
    await sendDeleteRequest(API_PENDING_ORDER(companyId, userId));

    return true;
  } catch (error) {
    logMediumError(
      `Failed to delete pending order for user (${userId}) for company (${companyId}) ${handleJoiSchemaError(
        error
      )}`
    );
    throw error;
  }
};

export const deletePendingOrderBasket = async (
  userId: string
): Promise<boolean> => {
  const { companyId } = store.getState().configurationReducer;
  try {
    await sendDeleteRequest(API_PENDING_ORDER_BASKET(companyId, userId));

    return true;
  } catch (error) {
    logMediumError(
      `Failed to delete pending order basket for user (${userId}) for company (${companyId}) ${handleJoiSchemaError(
        error
      )}`
    );
    throw error;
  }
};

export const deletePendingOrderBasketItem = async (
  userId: string,
  itemKey: string
): Promise<boolean> => {
  const { companyId } = store.getState().configurationReducer;
  try {
    await sendDeleteRequest(
      API_PENDING_ORDER_BASKET_ITEM(companyId, userId, itemKey)
    );
    return true;
  } catch (error) {
    logHighError(
      `Failed to delete pending order basket iem ${itemKey} for user (${userId}) for company (${companyId}) ${handleJoiSchemaError(
        error
      )}`
    );
    throw error;
  }
};

export const getPendingOrder = async (userId: string) => {
  const { companyId } = store.getState().configurationReducer;
  try {
    const response = await sendGetRequest(
      API_PENDING_ORDER(companyId, userId),
      null,
      false
    );
    return response.data;
  } catch (error) {
    logHighError(
      `Failed to get pending order for user (${userId}) for company (${companyId}) ${handleJoiSchemaError(
        error
      )}`
    );
    throw error;
  }
};

export const checkPendingOrderValidity = async (): Promise<{
  basketUpdated?: boolean;
  pendingOrderDeleted?: boolean;
  message?: string;
  totalPriceOrder?: number;
}> => {
  const { companyId } = store.getState().configurationReducer;
  const {
    user: { customerId },
  } = store.getState().userReducer;
  if (!companyId || !customerId) {
    return;
  }
  try {
    const { data } = await sendPostRequest(
      API_PENDING_ORDER_VALIDITY_CHECK(companyId, customerId),
      null,
      false
    );
    return data;
  } catch (error) {
    logHighError(
      `Error checking pending order validity for user (${customerId}) for company (${companyId}) ${handleJoiSchemaError(
        error
      )}`
    );
    throw error;
  }
};
